// WHAT: Settings view component for configuring Withings API OAuth credentials.
// WHY:  Provides users with a secure interface to manage their Withings Client ID and Client Secret for OAuth authentication and health data synchronization.
// DOMAIN: Settings → Withings
// DEPENDENCIES: React, @/domains/health/withings/services, @/components/ui, @/domains/health/withings/types/domain, lucide-react
// RISKS:
//   - WARN: API credentials are stored in secure storage; backend services (`saveWithingsConfig`, `loadWithingsConfig`, `clearWithingsConfig`) are critical.
//   - NOTE: Client Secret input is masked; credentials are never displayed in the frontend after saving.
//   - WARN: Redirect URI must match the Withings app configuration; mismatches will cause OAuth failures.

import type { WithingsAuthConfig } from "@/domains/health/withings/types/domain";

import {
  Key,
  Settings,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Shield,
  Edit3,
  X,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";

import {
  Card,
  Button,
  Input,
  Label,
  Stack,
  Heading,
  Badge,
  StatusIndicator,
} from "@/components/ui";
import {
  saveWithingsConfig,
  loadWithingsConfig,
  clearWithingsConfig,
} from "@/domains/health/withings/services";

/**
 * WHAT:
 *   Settings view component for configuring Withings API OAuth credentials.
 *
 * WHY:
 *   Provides users with a secure interface to manage their Withings Client ID and Client Secret for OAuth authentication and health data synchronization.
 *
 * BEHAVIOR:
 *   - Loads current Withings configuration on mount via `loadWithingsConfig`.
 *   - Provides form for entering/updating Client ID, Client Secret, and Redirect URI.
 *   - Supports saving, editing, and removing credentials.
 *   - Displays configuration status and security information.
 *   - Clears sensitive fields after successful save for security.
 *
 * RISKS:
 *   - WARN: API credentials are stored in secure storage; backend services are critical.
 *   - NOTE: Client Secret input is masked; credentials are never displayed in the frontend after saving.
 *   - WARN: Redirect URI must match the Withings app configuration; mismatches will cause OAuth failures.
 *
 * @returns {JSX.Element} Withings API configuration component
 *
 */
// Reason: Component is used internally by WithingsSettingsView via routing, not directly imported

export function WithingsApiConfig() {
  /**
   * WHAT:
   *   Represents the subset of Withings configuration values that may be
   *   persisted and loaded from secure storage.
   *
   * WHY:
   *   The underlying `loadWithingsConfig`/`saveWithingsConfig` services are
   *   currently untyped and would otherwise be treated as `any`/`unknown`.
   *   By defining an explicit type and safe wrapper functions, we avoid
   *   unsafe calls and member access while keeping the component strictly typed.
   *
   * RISKS:
   *   - NOTE: This type mirrors `WithingsAuthConfig` but allows partial data
   *     because secure storage may only contain a subset of fields.
   */
  type StoredWithingsConfig = Partial<WithingsAuthConfig> & {
    redirectUri?: string;
    authUrl?: string;
    tokenUrl?: string;
  };

  /**
   * WHAT:
   *   Safely loads the Withings configuration from the shared service with
   *   proper TypeScript typing.
   *
   * WHY:
   *   The external service is currently untyped (`any`), which violates the
   *   linting rule `@typescript-eslint/no-unsafe-*`. This wrapper constrains
   *   the return type to a known structure and performs a runtime object check.
   *
   * OUTPUT:
   *   - `StoredWithingsConfig | null` – sanitized configuration payload.
   */
  const loadWithingsConfigSafe = useCallback(async (): Promise<null | StoredWithingsConfig> => {
    try {
      const raw = await (loadWithingsConfig as () => Promise<null | WithingsAuthConfig>)();

      if (!raw || typeof raw !== "object") {
        return null;
      }

      const config: WithingsAuthConfig = raw;
      const { authUrl, clientId, clientSecret, redirectUri, tokenUrl } = config;

      return {
        authUrl,
        clientId,
        clientSecret,
        redirectUri,
        tokenUrl,
      };
    } catch {
      return null;
    }
  }, []);

  /**
   * WHAT:
   *   Typed facade around `saveWithingsConfig`.
   *
   * WHY:
   *   Avoids unsafe calls on an `any`-typed import and makes the intent
   *   of the call clear at the component level.
   */
  const saveWithingsConfigSafe = useCallback(async (config: WithingsAuthConfig): Promise<void> => {
    await (saveWithingsConfig as (config: WithingsAuthConfig) => Promise<void>)(config);
  }, []);

  /**
   * WHAT:
   *   Typed facade around `clearWithingsConfig`.
   *
   * WHY:
   *   Ensures the call site is fully typed and passes ESLint's
   *   `no-unsafe-call` rule.
   */
  const clearWithingsConfigSafe = useCallback(async (): Promise<void> => {
    await (clearWithingsConfig as () => Promise<void>)();
  }, []);

  const [config, setConfig] = useState<WithingsAuthConfig>({
    authUrl: "https://account.withings.com/oauth2_user/authorize2",
    clientId: "",
    clientSecret: "",
    redirectUri: "tracq://oauth/withings/callback",
    tokenUrl: "https://wbsapi.withings.net/v2/oauth2",
  });
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState<string | undefined>();

  /**
   * WHAT:
   *   Updates OAuth URLs in configuration state from loaded config.
   *
   * WHY:
   *   Separates state update logic to reduce cognitive complexity.
   *
   * @param loadedConfig - Loaded configuration with OAuth URLs
   *
   */
  const updateOAuthUrls = useCallback((loadedConfig: StoredWithingsConfig) => {
    setConfig((previous) => ({
      ...previous,
      authUrl: loadedConfig.authUrl ?? previous.authUrl,
      redirectUri: loadedConfig.redirectUri ?? previous.redirectUri,
      tokenUrl: loadedConfig.tokenUrl ?? previous.tokenUrl,
    }));
  }, []);

  /**
   * WHAT:
   *   Processes loaded configuration and updates component state.
   *
   * WHY:
   *   Separates configuration processing logic to reduce cognitive complexity.
   *
   * @param loadedConfig - Loaded configuration from secure storage
   *
   */
  const processLoadedConfig = useCallback(
    (loadedConfig: null | StoredWithingsConfig) => {
      if (loadedConfig?.clientId && loadedConfig.clientSecret) {
        setIsConfigured(true);
        updateOAuthUrls(loadedConfig);
      } else {
        setIsConfigured(false);
      }
    },
    [updateOAuthUrls],
  );

  /**
   * WHAT:
   *   Loads Withings configuration from secure storage on component mount.
   *
   * WHY:
   *   Retrieves existing configuration to determine if credentials are already configured.
   *
   * SIDE EFFECTS:
   *   - Fetches configuration from secure storage
   *   - Updates configuration status
   *   - Updates OAuth URLs if present in loaded config
   *
   * RISKS:
   *   - NOTE: Client ID and Secret are intentionally not loaded into state for security
   *   - WARN: Fails silently if configuration cannot be loaded
   */
  useEffect(() => {
    /**
     * WHAT:
     *   Async loader that wraps the untyped service call with a typed
     *   interface and guarded state updates.
     *
     * WHY:
     *   Keeps the effect body type-safe and avoids chaining `.then/.catch`
     *   on an `any`-typed value, which violates strict ESLint rules.
     */
    const loadConfig = async () => {
      try {
        const loadedConfig = await loadWithingsConfigSafe();
        processLoadedConfig(loadedConfig);
      } catch {
        setIsConfigured(false);
      }
    };

    void loadConfig();
  }, [loadWithingsConfigSafe, processLoadedConfig]);

  /**
   * WHAT:
   *   Saves the entered Withings API credentials to secure storage.
   *
   * WHY:
   *   To persist credentials for future OAuth authentication and health data synchronization.
   *
   * BEHAVIOR:
   *   - Validates that both Client ID and Client Secret are provided.
   *   - Invokes `saveWithingsConfig` with the current config.
   *   - Clears sensitive fields (Client ID, Client Secret) after successful save.
   *   - Exits edit mode and shows success message.
   *
   * RISKS:
   *   - WARN: Credentials are stored in secure storage; backend service is critical.
   *   - NOTE: Form validation ensures both fields are non-empty before saving.
   */
  /**
   * WHAT:
   *   Validates that required credentials are provided.
   *
   * WHY:
   *   Separates validation logic to reduce cognitive complexity.
   *
   * @returns {boolean} True if credentials are valid
   *
   */
  const validateCredentials = (): boolean => {
    if (!config.clientId || !config.clientSecret) {
      setError("❌ Client ID and Client Secret are required.");
      return false;
    }
    return true;
  };

  /**
   * WHAT:
   *   Clears sensitive fields from configuration state.
   *
   * WHY:
   *   Separates state update logic to reduce cognitive complexity.
   *
   */
  const clearSensitiveFields = () => {
    setConfig((previous) => ({
      ...previous,
      clientId: "",
      clientSecret: "",
    }));
  };

  /**
   * WHAT:
   *   Saves Withings OAuth configuration to secure storage.
   *
   * WHY:
   *   Persists credentials for OAuth authentication and updates configuration status.
   *
   * SIDE EFFECTS:
   *   - Validates required fields (Client ID and Secret)
   *   - Saves configuration to secure storage
   *   - Clears sensitive fields from component state after save
   *   - Updates configuration status
   *   - Displays success/error messages
   *
   * RISKS:
   *   - WARN: No validation of credential format or OAuth URL structure
   *   - NOTE: Sensitive fields are cleared from state after save for security
   *   - WARN: Error messages may not provide specific failure reasons
   */
  const handleSave = async () => {
    if (!validateCredentials()) {
      return;
    }

    setSaving(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      await saveWithingsConfigSafe(config);
      setSuccess("✅ Withings configuration saved successfully!");
      setIsConfigured(true);
      setIsEditing(false);
      clearSensitiveFields();
    } catch (error_) {
      const errorMessage =
        error_ instanceof Error ? error_.message : "❌ Error saving configuration.";
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(undefined);
    setSuccess(undefined);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setConfig((previous) => ({
      ...previous,
      clientId: "",
      clientSecret: "",
    }));
    setError(undefined);
    setSuccess(undefined);
  };

  /**
   * WHAT:
   *   Removes stored Withings API credentials from secure storage.
   *
   * WHY:
   *   To allow users to disconnect their Withings account.
   *
   * BEHAVIOR:
   *   - Invokes `clearWithingsConfig` to remove credentials.
   *   - Updates configuration status to not configured.
   *   - Exits edit mode and shows success message.
   *
   * RISKS:
   *   - NOTE: No confirmation dialog is shown; credentials are immediately removed.
   */
  const handleRemove = async () => {
    try {
      setSaving(true);
      setError(undefined);
      setSuccess(undefined);

      await clearWithingsConfigSafe();
      setIsConfigured(false);
      setIsEditing(false);
      setSuccess("✅ Configuration removed successfully!");
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error removing configuration.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * WHAT:
   *   Renders configuration form section.
   *
   * WHY:
   *   Separates render logic to reduce cognitive complexity.
   *
   * @returns {JSX.Element | null} Configuration form section
   *
   */
  const renderConfigurationForm = () => {
    if (isConfigured && !isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          {/* Header */}
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Settings className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              {isConfigured ? "Update Withings Configuration" : "Configure Withings API"}
            </Heading>
          </Stack>

          {/* Client ID Field */}
          <Stack direction="col" gap={2}>
            <Label className="text-slate-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              <span>Client ID</span>
              <span className="text-red-400">*</span>
            </Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              onChange={(e) => {
                setConfig({ ...config, clientId: e.target.value });
              }}
              placeholder="Enter your Withings Client ID..."
              size="md"
              type="text"
              value={config.clientId}
            />
          </Stack>

          {/* Client Secret Field */}
          <Stack direction="col" gap={2}>
            <Label className="text-slate-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              <span>Client Secret</span>
              <span className="text-red-400">*</span>
            </Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              onChange={(e) => {
                setConfig({ ...config, clientSecret: e.target.value });
              }}
              placeholder="Enter your Withings Client Secret..."
              size="md"
              type="password"
              value={config.clientSecret}
            />
          </Stack>

          {/* Redirect URI Field */}
          <Stack direction="col" gap={2}>
            <Label className="text-slate-300">Redirect URI</Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              onChange={(e) => {
                setConfig({ ...config, redirectUri: e.target.value });
              }}
              placeholder="tracq://oauth/withings/callback"
              size="md"
              type="text"
              value={config.redirectUri}
            />
            <p className="text-slate-500 text-xs">
              This should match your Withings app configuration
            </p>
          </Stack>

          {/* Action Buttons */}
          <Stack className="flex-wrap" direction="row" gap={2}>
            <Button
              disabled={loading || saving || !config.clientId || !config.clientSecret}
              onClick={() => {
                void handleSave();
              }}
              size="md"
              variant="primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isConfigured ? "Update Configuration" : "Save Configuration"}
                </>
              )}
            </Button>

            {isEditing ? (
              <Button disabled={saving} onClick={handleCancel} size="md" variant="ghost">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Card>
    );
  };

  /**
   * WHAT:
   *   Renders configured state section.
   *
   * WHY:
   *   Separates render logic to reduce cognitive complexity.
   *
   * @returns {JSX.Element | null} Configured state section
   *
   */
  const renderConfiguredState = () => {
    if (!isConfigured || isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          {/* Section Header */}
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                Withings Connected
              </Heading>
            </Stack>

            <Stack direction="row" gap={2}>
              <Button onClick={handleEdit} size="md" variant="ghost">
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                disabled={saving}
                onClick={() => {
                  void handleRemove();
                }}
                size="md"
                variant="danger"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Remove
              </Button>
            </Stack>
          </Stack>

          {/* Connection Status */}
          <div className="space-y-2">
            <Label className="text-slate-400 text-sm">Connection Status</Label>
            <Badge size="sm" style="subtle" variant="success">
              ✓ Connected
            </Badge>
          </div>

          {/* Management Content */}
          <Stack direction="col" gap={2}>
            <p className="text-slate-300 text-sm">
              Your Withings scale is connected and ready to sync weight, body composition, and
              health metrics.
            </p>
            <p className="text-slate-500 text-xs">
              The API credentials are securely stored and will be used for OAuth authentication.
            </p>
          </Stack>
        </Stack>
      </Card>
    );
  };

  /**
   * WHAT:
   *   Renders status messages section.
   *
   * WHY:
   *   Separates render logic to reduce cognitive complexity.
   *
   * @returns {JSX.Element | null} Status messages section
   *
   */
  const renderStatusMessages = () => {
    return (
      <>
        {success ? (
          <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
            <Stack align="center" direction="row" gap={3}>
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-300 text-sm font-medium">{success}</span>
            </Stack>
          </Card>
        ) : null}

        {error ? (
          <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
            <Stack align="center" direction="row" gap={3}>
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-300 text-sm font-medium">{error}</span>
            </Stack>
          </Card>
        ) : null}

        {loading ? (
          <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
            <Stack align="center" direction="row" gap={3}>
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
              <span className="text-slate-400 text-sm">Loading Withings configuration...</span>
            </Stack>
          </Card>
        ) : null}
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Key className="w-5 h-5 text-slate-400" />
          API Configuration Status
        </Heading>

        <StatusIndicator
          description="Your Withings Client ID and Client Secret for OAuth integration"
          isConfigured={isConfigured}
          label="Withings API Credentials"
          showLock
          size="md"
        />
      </div>

      {/* Information Card */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">Withings Scale Integration</h3>
            <p className="text-sm text-blue-300">
              Connect your Withings scale to automatically sync weight, body composition, and health
              metrics. Your API credentials are stored securely and used for OAuth authentication.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Configuration Form */}
      {renderConfigurationForm()}

      {/* Configured State */}
      {renderConfiguredState()}

      {/* How to Get Credentials */}
      <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-1">
              How to get your Withings API credentials
            </h3>
            <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
              <li>
                Go to{" "}
                <a
                  className="text-tracq-bright-blue hover:underline"
                  href="https://developer.withings.com"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Withings Developer Portal
                </a>
              </li>
              <li>Create a new application</li>
              <li>
                Set the redirect URI to:{" "}
                <code className="bg-slate-700 px-1 rounded">tracq://oauth/withings/callback</code>
              </li>
              <li>Copy the Client ID and Client Secret</li>
              <li>Paste them in the fields above</li>
            </ol>
          </div>
        </Stack>
      </Card>

      {/* Status Messages */}
      {renderStatusMessages()}

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Privacy</h3>
            <p className="text-sm text-green-300">
              Your Withings API credentials are stored securely in Tauri&apos;s encrypted storage.
              No sensitive data is ever displayed in the frontend, and all API calls are made
              directly from the secure backend.
            </p>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
